import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Sparkles } from "lucide-react";
import styled from "styled-components";
import { Button } from "../ui/Button";
import { ThemeToggle } from "../ThemeToggle";
import { LanguageSwitcher } from "../LanguageSwitcher";

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.glass?.light?.background || "hsla(0, 0%, 100%, 0.8)"};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};

  body.dark & {
    background: ${({ theme }) => theme.colors.glass?.dark?.background || "hsla(240, 15%, 10%, 0.8)"};
    border-bottom-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 0 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 0 32px;
  }
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    height: 80px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.soft};
  transition: box-shadow 0.3s ease;

  ${LogoLink}:hover & {
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
`;

const LogoText = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 700;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const DesktopNav = styled.nav`
  display: none;
  align-items: center;
  gap: 4px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  padding: 8px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  color: ${({ theme, $active }) =>
    $active
      ? theme.colors.light?.primary || theme.colors.primary.light
      : theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  background: ${({ theme, $active }) =>
    $active ? `${theme.colors.light?.primary || theme.colors.primary.light}10` : "transparent"};

  body.dark & {
    color: ${({ theme, $active }) =>
      $active
        ? theme.colors.dark?.primary || theme.colors.primary.light
        : theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
    background: ${({ theme, $active }) =>
      $active ? `${theme.colors.dark?.primary || theme.colors.primary.light}10` : "transparent"};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
    background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"};

    body.dark & {
      color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
      background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"};
    }
  }
`;

const DesktopActions = styled.div`
  display: none;
  align-items: center;
  gap: 12px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: flex;
  }
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
  cursor: pointer;
  transition: background 0.2s ease;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"};

    body.dark & {
      background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"};
    }
  }
`;

const MobileMenu = styled.div`
  display: ${({ $open }) => ($open ? "flex" : "none")};
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
  animation: fade-up 0.3s ease-out;
`;

const MobileNavLink = styled(Link)`
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  color: ${({ theme, $active }) =>
    $active
      ? theme.colors.light?.primary || theme.colors.primary.light
      : theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  background: ${({ theme, $active }) =>
    $active ? `${theme.colors.light?.primary || theme.colors.primary.light}10` : "transparent"};

  body.dark & {
    color: ${({ theme, $active }) =>
      $active
        ? theme.colors.dark?.primary || theme.colors.primary.light
        : theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
    background: ${({ theme, $active }) =>
      $active ? `${theme.colors.dark?.primary || theme.colors.primary.light}10` : "transparent"};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
    background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"};

    body.dark & {
      color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
      background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"};
    }
  }
`;

const MobileButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const navLinkKeys = [
  { key: "nav.features", href: "/features" },
  { key: "nav.pricing", href: "/pricing" },
  { key: "nav.about", href: "/about" },
  { key: "nav.contact", href: "/contact" },
];

export function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <Nav>
      <NavContainer>
        <NavInner>
          <LogoLink to="/">
            <IconBox>
              <Sparkles size={20} style={{ color: "white" }} />
            </IconBox>
            <LogoText>{t("common.storeHub")}</LogoText>
          </LogoLink>

          <DesktopNav>
            {navLinkKeys.map((link) => (
              <NavLink key={link.key} to={link.href} $active={location.pathname === link.href}>
                {t(link.key)}
              </NavLink>
            ))}
          </DesktopNav>

          <DesktopActions>
            <ThemeToggle />
            <LanguageSwitcher />
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Button variant="ghost" size="sm">
                {t("nav.signIn")}
              </Button>
            </Link>
            <Link to="/signup" style={{ textDecoration: "none" }}>
              <Button variant="gradient" size="sm">
                {t("nav.getStarted")}
              </Button>
            </Link>
          </DesktopActions>

          <MobileActions>
            <ThemeToggle />
            <LanguageSwitcher />
            <MobileMenuButton onClick={() => setIsOpen(!isOpen)} aria-label={t("nav.toggleMenu")}>
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </MobileMenuButton>
          </MobileActions>
        </NavInner>

        <MobileMenu $open={isOpen}>
          {navLinkKeys.map((link) => (
            <MobileNavLink
              key={link.key}
              to={link.href}
              $active={location.pathname === link.href}
              onClick={() => setIsOpen(false)}
            >
              {t(link.key)}
            </MobileNavLink>
          ))}
          <MobileButtons>
            <Link to="/login" style={{ textDecoration: "none", flex: 1 }} onClick={() => setIsOpen(false)}>
              <Button variant="ghost" fullWidth>
                {t("nav.signIn")}
              </Button>
            </Link>
            <Link to="/signup" style={{ textDecoration: "none", flex: 1 }} onClick={() => setIsOpen(false)}>
              <Button variant="gradient" fullWidth>
                {t("nav.getStarted")}
              </Button>
            </Link>
          </MobileButtons>
        </MobileMenu>
      </NavContainer>
    </Nav>
  );
}
